import React from "react"
import { ConfigProvider, Form, Table, message, Tooltip } from "antd"
import type { ColumnsType } from 'antd/es/table'
import { useAntdTable } from "ahooks"
import FilterBar from "./FilterBar"
import classnames from 'classnames'
import './index.scss'
import zh_CN from "antd/es/locale/zh_CN"
import {
  StepBackwardOutlined,
  StepForwardOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'

export type QueryTableRef<R = any> = {
  reload: () => void
  reset: () => void
  changePage: (next: { current?: number; pageSize?: number }) => void
}

interface QueryTableProps {
  rowSelection?: any
  columns: ColumnsType<any>
  remoteUrl: string
  extraParams?: Record<string, any>
  filterBarOptions?: any[]
  filterBarMoreActions?: React.ReactNode
  filterBarPosition?: 'left' | 'right'
  filterBarWrapperClassName?: string
  filterBarWrapperStyle?: React.CSSProperties
  onRowClick?: (record: any, event: React.MouseEvent<HTMLTableRowElement>) => void
  antdTableProps?: any
  defaultSortField?: string
  defaultSortOrder?: 'asc' | 'desc'
  showTopPagination?: boolean
  showBottomPagination?: boolean
}

interface Result {
  list: any[]
  total: number,
}

const QueryTable = React.forwardRef<QueryTableRef, QueryTableProps>(({
  rowSelection,
  columns,
  remoteUrl,
  extraParams,
  filterBarOptions,
  filterBarMoreActions,
  filterBarPosition = 'left',
  filterBarWrapperClassName = 'mb-4',
  filterBarWrapperStyle,
  onRowClick,
  antdTableProps,
  defaultSortField = 'id',
  defaultSortOrder = 'desc',
  showTopPagination = true,
  showBottomPagination = true,
}, ref) => {
  const [form] = Form.useForm()
  const rowKey = antdTableProps?.rowKey ? antdTableProps.rowKey : 'keyIndex'
  const [activeRowKey, setActiveRowKey] = React.useState<React.Key>()
  const extraParamsMountedRef = React.useRef(false)
  const [currentSort, setCurrentSort] = React.useState<{field?: string, order?: string}>({
    field: defaultSortField,
    order: defaultSortOrder
  })

  const getTableData = React.useCallback((
    { current, pageSize },
    formData: Object
  ): Promise<Result> => {
    let query = `page=${current}&size=${pageSize}`

    if (currentSort.field && currentSort.order) {
      query += `&sort_field=${currentSort.field}&sort_order=${currentSort.order}`
    }

    Object.entries(formData).forEach(([key, value]) => {
      if (value) {
        query += `&${key}=${value}`;
      }
    })

    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query += `&${key}=${value}`;
      }
    })

    return fetch(`${remoteUrl}?${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.code === 0) {
          return {
            total: res.total,
            list: res.data.items,
          }
        } else {
          res.message && message.error(res.message)
        }
      })
  }, [currentSort.field, currentSort.order, remoteUrl, extraParams])

  const { tableProps, search, pagination } = useAntdTable(getTableData, {
    defaultPageSize: 20,
    form,
    defaultParams: [{
      current: 1,
      pageSize: 20,
    }, {}]
  })

  const total = tableProps.pagination?.total ?? pagination.total ?? 0
  const current = tableProps.pagination?.current ?? pagination.current ?? 1
  const pageSize = tableProps.pagination?.pageSize ?? pagination.pageSize ?? 20
  const totalPage = Math.max(1, Math.ceil(Number(total || 0) / Number(pageSize || 1)))

  const changePage = React.useCallback((next: { current?: number; pageSize?: number }) => {
    const nextCurrent = next.current ?? current
    const nextPageSize = next.pageSize ?? pageSize
    if (pagination.changeCurrent) {
      pagination.changeCurrent(Number(nextCurrent))
    }
    if (next.pageSize !== undefined && pagination.changePageSize) {
      pagination.changePageSize(Number(nextPageSize))
    }
  }, [current, pageSize, pagination])

  const goFirst = React.useCallback(() => changePage({ current: 1 }), [changePage])
  const goLast = React.useCallback(() => changePage({ current: totalPage }), [changePage, totalPage])
  const goPrev = React.useCallback(
    () => changePage({ current: Math.max(1, Number(current) - 1) }),
    [changePage, current],
  )
  const goNext = React.useCallback(
    () => changePage({ current: Math.min(totalPage, Number(current) + 1) }),
    [changePage, current, totalPage],
  )

  // 处理表格变化（分页、排序、筛选）
  const handleTableChange = (nextPagination: any, filters: any, sorter: any) => {
    // 处理排序状态更新
    const { field, order } = sorter

    let sortOrder = ''
    if (order === 'ascend') {
      sortOrder = 'asc'
    } else if (order === 'descend') {
      sortOrder = 'desc'
    }

    // 检查是否是排序变化（与当前排序状态比较）
    const isSortChange = field !== currentSort.field || sortOrder !== currentSort.order

    if (isSortChange && field && order) {
      // 排序变化：更新排序状态并重新请求第一页
      const newSort = { field, order: sortOrder }
      setCurrentSort(newSort)
      setTimeout(() => {
        search.submit()
      }, 0)
    } else {
      // 分页或其他变化：调用原始的 onChange
      if (tableProps.onChange) {
        tableProps.onChange(nextPagination, filters, sorter)
      }
    }
  }

  const rowClassName = React.useCallback(
    (record: Record<string, any>, index: number, indent: number) => {
      const propsClassName =
        typeof tableProps?.rowClassName === 'function'
          ? tableProps?.rowClassName(record, index, indent)
          : tableProps?.rowClassName

      if (onRowClick) {
        const _rowKey = typeof rowKey === 'function' ? rowKey(record) : record[rowKey]

        return classnames({
          [propsClassName!]: !!propsClassName,
          ['query-table-click-row']: true,
          ['query-table-active-row']: activeRowKey === _rowKey,
        })
      } else {
        return propsClassName!
      }
    },
    [tableProps?.rowClassName, onRowClick, activeRowKey]
  )

  React.useImperativeHandle(
    ref,
    () => ({
      reload: () => search.submit(),
      reset: search.reset,
      changePage,
    }),
    [search.submit, search.reset, changePage]
  )

  React.useEffect(() => {
    if (!extraParamsMountedRef.current) {
      extraParamsMountedRef.current = true
      return
    }
    search.submit()
  }, [extraParams, search.submit])

  const topPagerNav = showTopPagination ? (
    <div
      className="flex items-center gap-1"
      style={{ color: '#595959', fontSize: 13 }}
    >
      <Tooltip title="到头">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) > 1 ? goFirst : undefined}
          className={`flex items-center justify-center px-1.5 h-6 rounded cursor-pointer select-none ${Number(current) > 1 ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <StepBackwardOutlined />
          <span className="ml-1">到头</span>
        </span>
      </Tooltip>
      <Tooltip title="前页">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) > 1 ? goPrev : undefined}
          className={`flex items-center justify-center px-1.5 h-6 rounded cursor-pointer select-none ${Number(current) > 1 ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <LeftOutlined />
          <span className="ml-1">前页</span>
        </span>
      </Tooltip>
      <Tooltip title="后页">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) < totalPage ? goNext : undefined}
          className={`flex items-center justify-center px-1.5 h-6 rounded cursor-pointer select-none ${Number(current) < totalPage ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <RightOutlined />
          <span className="ml-1">后页</span>
        </span>
      </Tooltip>
      <Tooltip title="到尾">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) < totalPage ? goLast : undefined}
          className={`flex items-center justify-center px-1.5 h-6 rounded cursor-pointer select-none ${Number(current) < totalPage ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <StepForwardOutlined />
          <span className="ml-1">到尾</span>
        </span>
      </Tooltip>
    </div>
  ) : null

  const topPagerInfo = showTopPagination ? (
    <div style={{ color: '#8c8c8c', fontSize: 13 }}>
      第{current}页，共{totalPage}页，共{total}条
    </div>
  ) : null

  const bottomPagerNav = showBottomPagination ? (
    <div
      className="flex items-center gap-1"
      style={{ color: '#595959', fontSize: 13 }}
    >
      <Tooltip title="到头">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) > 1 ? goFirst : undefined}
          className={`flex items-center justify-center px-1.5 h-6 cursor-pointer select-none ${Number(current) > 1 ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <StepBackwardOutlined />
          <span className="ml-1">到头</span>
        </span>
      </Tooltip>
      <Tooltip title="前页">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) > 1 ? goPrev : undefined}
          className={`flex items-center justify-center px-1.5 h-6 cursor-pointer select-none ${Number(current) > 1 ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <LeftOutlined />
          <span className="ml-1">前页</span>
        </span>
      </Tooltip>
      <Tooltip title="后页">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) < totalPage ? goNext : undefined}
          className={`flex items-center justify-center px-1.5 h-6 cursor-pointer select-none ${Number(current) < totalPage ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <RightOutlined />
          <span className="ml-1">后页</span>
        </span>
      </Tooltip>
      <Tooltip title="到尾">
        <span
          role="button"
          tabIndex={0}
          onClick={Number(current) < totalPage ? goLast : undefined}
          className={`flex items-center justify-center px-1.5 h-6 cursor-pointer select-none ${Number(current) < totalPage ? 'hover:bg-gray-100' : 'opacity-40 cursor-not-allowed'}`}
        >
          <StepForwardOutlined />
          <span className="ml-1">到尾</span>
        </span>
      </Tooltip>
    </div>
  ) : null

  const bottomPagerInfo = showBottomPagination ? (
    <div style={{ color: '#8c8c8c', fontSize: 13 }}>
      第{current}页，共{totalPage}页，共{total}条
    </div>
  ) : null

  return (
    <ConfigProvider locale={zh_CN}>
      {(filterBarOptions || filterBarMoreActions || showTopPagination) && (
        <div
          className={`flex items-center justify-between gap-4 ${filterBarWrapperClassName}`}
          style={filterBarWrapperStyle}
        >
          {filterBarPosition === 'right' ? (
            <>
              <div className="flex items-center flex-shrink-0 gap-4">
                {topPagerNav}
                {filterBarMoreActions && (
                  <div className="flex-shrink-0">
                    {filterBarMoreActions}
                  </div>
                )}
              </div>
              <div className="flex flex-1 justify-end">
                <FilterBar
                  filterBarOptions={filterBarOptions}
                  submit={search.submit}
                  reset={search.reset}
                  form={form}
                  showSearchButton={false}
                  autoSearch={true}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center flex-1">
                {topPagerNav}
                <div className="flex-1">
                  <FilterBar
                    filterBarOptions={filterBarOptions}
                    submit={search.submit}
                    reset={search.reset}
                    form={form}
                    showSearchButton={false}
                    autoSearch={true}
                  />
                </div>
                {filterBarMoreActions && (
                  <div className="flex-shrink-0">
                    {filterBarMoreActions}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {topPagerInfo && (
        <div className="flex justify-end mb-2" style={{ color: '#8c8c8c' }}>
          {topPagerInfo}
        </div>
      )}
      <div className="query_table_list border border-gray-200 rounded-none overflow-hidden mb-0">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          locale={{
            emptyText: '暂无数据'
          }}
          rowKey="id"
          className="whitespace-nowrap overflow-x-auto text-ellipsis"
          onRow={(record) => {
            return {
              onClick: (e) => {
                e.stopPropagation()
                const selection = window.getSelection()!.toString()
                if (selection.length <= 0) {
                  onRowClick?.(record, e)
                  setActiveRowKey(typeof rowKey === 'function' ? rowKey(record) : record[rowKey])
                }
              },
            };
          }}
          {...antdTableProps}
          {...{ ...tableProps, onChange: undefined, pagination: false }}
          onChange={handleTableChange}
          rowClassName={rowClassName}
          scroll={{
            x: 'max-content',
            scrollToFirstRowOnChange: true,
            ...tableProps?.scroll,
          }}
        />
      </div>
      {(bottomPagerNav || bottomPagerInfo) && (
        <div
          className="flex items-center justify-between gap-4 border border-t-0 border-gray-200 rounded-none bg-gray-50 px-3 py-1"
          style={{
            marginLeft: filterBarWrapperStyle?.marginLeft,
            marginRight: filterBarWrapperStyle?.marginRight,
            width: filterBarWrapperStyle?.width,
            maxWidth: filterBarWrapperStyle?.maxWidth,
            boxSizing: filterBarWrapperStyle?.boxSizing,
          }}
        >
          {bottomPagerNav}
          {bottomPagerInfo}
        </div>
      )}
    </ConfigProvider>
  )
})

export default QueryTable
